// ---------------------------------------------------------------------------
// Layout.js — Main Application Shell (Warm Stone Design System)
// Shadow Fund Admin
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import CommandPalette from "./CommandPalette";
import NeedsReviewPopup from "./NeedsReviewPopup";
import { waitingCount } from "./needsReview";
import { readSidebarPref, saveSidebarPref, nextSidebarPref, isRailWidth, sidebarClass } from "./sidebarPref";
import { useAuth } from "./AuthContext";
import t from "./theme";
import "./Layout.css";

// ── Navigation Structure ─────────────────────────────────────────────────
// Grouped by function for a clean, scannable sidebar
const navSections = [
  {
    label: null, // no label = top-level
    items: [
      // The ones Brian reads daily, in the order he asked for them: on
      // 21 September 2026, "let's move deal reports above cash owed", and on
      // 1 October 2026, "please also move bank feed above cash owed" — which
      // takes Bank Feed out of DATA & RECON, so it is listed once.
      // "/dashboard", not "/": the root redirects there, so a link to "/"
      // was never the active one and the Dashboard was the only page the
      // sidebar did not highlight.
      { to: "/dashboard", label: "Dashboard", icon: "◻" },
      { to: "/nav", label: "NAV / Performance", icon: "△" },
      { to: "/deal-reports", label: "Deal Reports", icon: "▥" },
      { to: "/bank-feed", label: "Bank Feed", icon: "◱" },
      { to: "/cash-owed", label: "Cash Owed", icon: "⊙" },
      // Everything waiting for a yes or a no, in one place, with its count.
      // After the run Brian ordered, not inside it. Brian, 27 October 2026:
      // "make an actual way for me to approve stuff easily. right now i have
      // to click into a deal and even then its not clear at all" (D-281).
      { to: "/approvals", label: "Approvals", icon: "✓", countKey: "approvals" },
    ],
  },
  {
    label: "PORTFOLIO",
    items: [
      { to: "/positions", label: "Positions", icon: "◈" },
      { to: "/warrants", label: "Warrant Tracker", icon: "◫" },
      { to: "/trade-blotter", label: "Trade Blotter", icon: "◇" },
      { to: "/cash-waterfall", label: "Cash Waterfall", icon: "▽" },
      { to: "/securities", label: "Securities", icon: "◆" },
      { to: "/deals", label: "Deals", icon: "◎" },
      { to: "/deal-wizard", label: "Deal Wizard", icon: "✦" },
    ],
  },
  {
    label: "ACCOUNTING",
    items: [
      { to: "/ledger", label: "Ledger", icon: "▤" },
      { to: "/investors", label: "Investors", icon: "▣" },
      { to: "/capital-calls", label: "Capital Calls", icon: "◪" },
      { to: "/fee-structure", label: "Fee Structure", icon: "◧" },
      { to: "/deal-team", label: "Deal Team", icon: "◍" },
    ],
  },
  {
    label: "ELOC",
    items: [
      { to: "/eloc-facilities", label: "Facilities", icon: "▧" },
      { to: "/eloc-draws", label: "ELOC Put Notices", icon: "▨" },
    ],
  },
  {
    label: "DATA & RECON",
    items: [
      // What the nightly notice watch found, waiting for a yes or a no
      // (20 October 2026). First in the section because it is read each morning.
      { to: "/notice-review", label: "Notice Review", icon: "✉" },
      { to: "/prime-broker", label: "Prime Broker", icon: "◰" },
      { to: "/data-pipeline", label: "Data Pipeline", icon: "⚙" },
    ],
  },
  {
    label: "ADMIN",
    items: [
      { to: "/entities", label: "Entities", icon: "◨" },
      { label: "Reports", icon: "▤", to: "/reports" },
      { to: "/settings", label: "Settings", icon: "◩" },
      { to: "/team", label: "Team", icon: "◉" },
    ],
  },
];

// ── Sidebar Nav Link ─────────────────────────────────────────────────────
// The label is always rendered and the stylesheet hides it on the icon rail,
// so the rail is decided by CSS at first paint. `title` carries the name for
// the rail, where the icon is all that shows.
function SidebarLink({ to, label, icon, count }) {
  const waiting = count > 0;
  return (
    <NavLink
      to={to}
      end={to === "/"}
      title={waiting ? `${label}: ${count} waiting` : label}
      aria-label={waiting ? `${label}, ${count} waiting` : undefined}
      className={({ isActive }) => `app-nav-link${isActive ? " active" : ""}`}
    >
      <span className="app-nav-icon" aria-hidden="true">{icon}</span>
      <span className="app-nav-label">{label}</span>
      {waiting && <span className="app-nav-count" aria-hidden="true">{count}</span>}
    </NavLink>
  );
}

// ── Main Layout ──────────────────────────────────────────────────────────
export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  // Full column, icon rail, or whatever the width suggests — remembered, so
  // the shell opens the way the reader left it (sidebarPref.js).
  const [sidebarPref, setSidebarPref] = useState(readSidebarPref);
  // The number on the Approvals link, from the pop-up's read of the list.
  const [counts, setCounts] = useState({ approvals: 0 });
  const onReviewData = useCallback((data) => setCounts({ approvals: waitingCount(data) }), []);
  useEffect(() => saveSidebarPref(sidebarPref), [sidebarPref]);
  // The drawer, on a phone. Closed on every navigation, because a menu
  // that stays open over the page it just opened is the commonest way an
  // off-canvas shell feels broken.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  // The global search (usability review, item 2). Cmd+K on a Mac, Ctrl+K
  // elsewhere, from any page.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const toggleSidebar = () => setSidebarPref((pref) => nextSidebarPref(pref, isRailWidth()));

  return (
    <div
      className={`app-shell${sidebarClass(sidebarPref)}${menuOpen ? " menu-open" : ""}`}
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: t.fontBody,
        background: t.bg,
        color: t.text,
      }}
    >
      {/* Tapping anywhere off the drawer closes it. */}
      <div
        className="app-scrim"
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />
      {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
      <nav className="app-sidebar" aria-label="Main navigation">
        {/* Logo / Brand */}
        <div className="app-brand">
          {/* The logo is itself the "Helena Partners" wordmark, so the name is
              not typeset beside it again; on the icon rail a monogram stands in
              for a wordmark that would be cut off. */}
          <img src="/helena_logo_light.png" alt="Helena Partners" className="app-brand-logo" />
          <span className="app-brand-mark" aria-hidden="true">H</span>
          <div className="app-brandtext">
            <div className="app-brandsub">Portfolio Management System</div>
          </div>
        </div>

        <button
          type="button"
          className="app-search-trigger"
          onClick={() => setSearchOpen(true)}
          title="Search deals, tickers, positions and pages (Ctrl+K or ⌘K)"
        >
          <span className="app-nav-icon" aria-hidden="true">⌕</span>
          <span className="app-nav-label">Search…</span>
          <kbd className="app-nav-label">{shortcutLabel()}</kbd>
        </button>

        {/* Navigation sections */}
        <div className="app-nav">
          {navSections.map((section, si) => (
            <div key={si} className="app-nav-group">
              {section.label && <div className="app-nav-section">{section.label}</div>}
              {si > 0 && <div className="app-nav-divider" aria-hidden="true" />}
              {section.items.map((item) => (
                <SidebarLink key={item.to} to={item.to} label={item.label} icon={item.icon}
                             count={item.countKey ? counts[item.countKey] : 0} />
              ))}
            </div>
          ))}
        </div>

        {/* User / collapse / sign out */}
        <div className="app-sidebar-foot">
          {user && (
            <div className="app-user app-nav-label" title={user.email || user.username}>
              {user.email || user.username || "User"}
            </div>
          )}
          <button
            type="button"
            className="app-collapse"
            onClick={toggleSidebar}
            title="Collapse or expand the sidebar"
          >
            <span className="app-nav-icon app-collapse-icon" aria-hidden="true">«</span>
            <span className="app-nav-label">Collapse sidebar</span>
          </button>
          <button type="button" className="app-signout" onClick={logout} title="Sign out">
            <span className="app-nav-icon" aria-hidden="true">⏻</span>
            <span className="app-nav-label">Sign Out</span>
          </button>
        </div>
      </nav>

      {/* ── MAIN CONTENT ────────────────────────────────────────────────── */}
      <main
        className="app-main"
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: "auto",
          background: t.bg,
        }}
      >
        {/* Phone top bar — the menu button and where you are. Shown only at
            phone width, where the sidebar is a drawer. */}
        <div className="app-mobilebar">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="app-menu-button"
          >
            {[0, 1, 2].map((i) => (
              <span key={i} />
            ))}
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="app-mobile-title">{getCurrentPageName(location.pathname)}</div>
            <div style={{ fontSize: 10.5, color: t.textMuted }}>Helena Partners</div>
          </div>
          <button type="button" className="app-menu-button app-mobile-search" aria-label="Search" onClick={() => setSearchOpen(true)}>⌕</button>
        </div>

        {/* Breadcrumb bar */}
        <div
          className="app-breadcrumb"
          style={{
            padding: "8px 28px",
            borderBottom: `1px solid ${t.borderLight}`,
            background: t.bgCard,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            fontSize: 12,
            color: t.textMuted,
            fontFamily: t.fontBody,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <span>Helena Partners</span>
            <span style={{ opacity: 0.4 }}>›</span>
            <span style={{ color: t.textSecondary, fontWeight: 500 }}>
              {getCurrentPageName(location.pathname)}
            </span>
          </div>
          <button type="button" className="app-topsearch" onClick={() => setSearchOpen(true)}>
            <span aria-hidden="true">⌕</span>
            <span className="app-topsearch-text">Search deals, tickers, pages…</span>
            <kbd>{shortcutLabel()}</kbd>
          </button>
          <div style={{ fontSize: 11, color: t.textDisabled, whiteSpace: "nowrap" }}>
            {new Date().toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>

        {/* Page content */}
        <div
          className="app-content"
          style={{
            padding: t.contentPadding,
            maxWidth: t.contentMaxWidth,
          }}
        >
          <Outlet />
        </div>
      </main>

      {/* What is waiting for review, by company and date: after sign-in, each
          day while anything waits, and when something new arrives (D-278). */}
      <NeedsReviewPopup onData={onReviewData} />

      <CommandPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        pages={navSections.flatMap((section) => section.items.map((item) => ({ ...item, section: section.label })))}
      />

      {/* Google Fonts — injected once */}
      <link href={t.fontLink} rel="stylesheet" />
    </div>
  );
}

// ⌘K on a Mac, Ctrl+K everywhere else — what the key on the reader's own
// keyboard is called.
function shortcutLabel() {
  const platform = typeof navigator === "undefined" ? "" : navigator.platform || navigator.userAgent || "";
  return /Mac|iPhone|iPad/.test(platform) ? "⌘K" : "Ctrl K";
}

// ── Helper: route path → page display name ───────────────────────────────
/** The page's name, read off the navigation rather than a second list.

    This was a hand-maintained map of path to label, and it had drifted: it
    never knew about Deal Reports, NAV / Performance or Cash Owed, so each of
    them fell through to the fallback and the breadcrumb read "cash-owed".
    Nobody saw it while the fallback only showed in a breadcrumb; the phone top
    bar puts the same string in the largest text on the screen.

    `navSections` already pairs every path with the label a reader clicked, so
    reading it there means one list, and a route added to the nav names itself
    (CLAUDE.md §0.21 — two lists of the same thing is one list and a bug
    waiting). The handful of pages reachable without a nav entry keep an
    explicit name below. */
const NAV_LABELS = Object.fromEntries(
  navSections.flatMap((section) =>
    section.items.map((item) => [item.to, item.label])),
);

const OFF_NAV_LABELS = {
  "/": "Dashboard",
  "/eloc-facilities": "ELOC Facilities",
  "/team": "Team Management",
  "/entities": "Entity Manager",
  "/settings": "Settings",
  "/cash-repatriation": "Cash Owed",   // the old path, still redirected
};

function getCurrentPageName(pathname) {
  const named = NAV_LABELS[pathname] || OFF_NAV_LABELS[pathname];
  if (named) return named;
  // Last resort: turn "/deal-wizard" into "Deal Wizard" rather than showing
  // the path, which is what a reader saw for every unmapped route.
  const slug = pathname.replace(/^\//, "").split("/")[0];
  if (!slug) return "Dashboard";
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

